import { QueryClient, type QueryKey, type UseSuspenseQueryResult, useSuspenseQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { attendanceSessionAdapter } from "@/3514/adapters"
import type { IAttendanceSessionDto as IDto, IAttendanceSessionModel as IModel } from "@/3514/types"
import { minutes, seconds } from "@/3514/utils/time.utils"

import { EAttendanceMarksQueryKey as EKey } from "./attendance.modal.api.config"
import fetchers from "./attendance.modal.api.queries.fetchers"

type TAttendanceMarksSuspenseQueryParams = { sessionId: number }
type TAttendanceMarksSuspenseQuery = UseSuspenseQueryResult<IModel, AxiosError>
function useAttendanceMarksSuspenseQuery({
    sessionId
}: TAttendanceMarksSuspenseQueryParams): ReturnType<
    (params: TAttendanceMarksSuspenseQueryParams) => TAttendanceMarksSuspenseQuery
> {
    return useSuspenseQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey({ sessionId }),
        queryFn: fetchers.attendanceMarks,
        select: attendanceSessionAdapter,
        staleTime: minutes(15),
        gcTime: minutes(5),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        retry: 1,
        retryDelay: seconds(5),
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

async function attendanceMarksQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { sessionId }: { sessionId: number }
): Promise<void> {
    await qc.prefetchQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey({ sessionId }),
        queryFn: fetchers.attendanceMarks,
        staleTime: minutes(15),
        gcTime: minutes(5),
        retry: false
    })
}

async function attendanceMarksQueryRemover(
    { qc }: { qc: QueryClient },
    { sessionId }: { sessionId: number }
): Promise<void> {
    await qc.cancelQueries({
        queryKey: EKey({ sessionId }),
        exact: true
    })
    await qc.resetQueries({
        queryKey: EKey({ sessionId }),
        exact: true
    })
}

export { useAttendanceMarksSuspenseQuery, attendanceMarksQueryPrefetcher, attendanceMarksQueryRemover }
export type { TAttendanceMarksSuspenseQuery }
